export const NETWORK_THRESHOLDS = {
    CPU_WARNING: 50,
    CPU_CRITICAL: 75,
    MEMORY_CRITICAL: 3000,
    SESSIONS_CRITICAL: 15,
    PACKET_LOSS_CRITICAL: 10,
    LATENCY_CRITICAL: 85,
    JITTER_CRITICAL: 20,
    BANDWIDTH_WARNING: 85,
    BANDWIDTH_CRITICAL: 20,
    THROUGHPUT_LOW: 80,
    THROUGHPUT_HIGH: 85,
    SINR_CRITICAL: 9,
    RSRQ_CRITICAL: -10,
    MAP_MARKER_LIMIT: 1000,
    STORE_NUMBER_LENGTH: 5,
} as const;

export const STATUS_CODES = {
    OK: 200,
    BAD_REQUEST: 400,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    INTERNAL_SERVER_ERROR: 500,
} as const;

export const BYTES_TO_GB_DIVISOR = 10_000_000;

export const VPN_STATUS = {
    NOT_FOUND: 'VPN_NOT_FOUND',
    BACKUP: 'VPN_BACKUP',
    PRIMARY: 'VPN_Primary',
} as const;

/**
 * Determines whether a FortiGate's active VPN tunnel name indicates it has
 * failed over to its backup (WAN2) circuit.
 *
 * `vpn_status` holds the *name* of the active IPsec tunnel reported by the
 * FortiGate, so the value varies by naming convention. Known backup tunnels:
 *   - Legacy: `VPN_BACKUP`
 *   - FortiManager-managed: `VPN_FMG_WAN2` (and other `*WAN2*` tunnels)
 * Anything that isn't the primary and isn't "not found" is treated as backup.
 */
export function isBackupVpnName(vpnStatus?: string): boolean {
    if (!vpnStatus) {
        return false;
    }
    const normalized = vpnStatus.toUpperCase();
    if (normalized === VPN_STATUS.NOT_FOUND.toUpperCase()) {
        return false;
    }
    if (normalized === VPN_STATUS.PRIMARY.toUpperCase()) {
        return false;
    }
    return normalized === VPN_STATUS.BACKUP.toUpperCase()
        || normalized.includes('WAN2')
        || normalized.includes('BACKUP');
}

/**
 * Returns true when `vpnStatus` names a real tunnel, as opposed to the
 * `VPN_NOT_FOUND` placeholder the dashboard substitutes when the lookup failed.
 */
export function hasResolvedVpnName(vpnStatus?: string): boolean {
    if (!vpnStatus) {
        return false;
    }
    return vpnStatus.toUpperCase() !== VPN_STATUS.NOT_FOUND.toUpperCase();
}

export const NETWORK_STATE = {
    ON_WAN1: 'ON_WAN1',
    ON_WAN2: 'ON_WAN2',
    EM_FAILOVER: 'EM_FAILOVER',
    OFFLINE: 'OFFLINE',
    UNKNOWN: 'UNKNOWN',
} as const;

export type NetworkState = typeof NETWORK_STATE[keyof typeof NETWORK_STATE];

export const NETWORK_STATE_LABELS: Record<NetworkState, string> = {
    ON_WAN1: 'On WAN1',
    ON_WAN2: 'On WAN2',
    EM_FAILOVER: 'EM Failover',
    OFFLINE: 'Offline',
    UNKNOWN: 'Circuit Unknown',
};

export const DEVICE_STATUS = {
    ONLINE: 'online',
    CONNECTED: 'Connected',
} as const;

export const LOCATION_STATUS = {
    FUTURE: 'future',
    REMODEL: 'remodel',
    CLOSED: 'closed',
} as const;

export const LAB_STORE_PREFIX = '9';

/** Live state of a single FortiGate WAN interface, from /fortigate/wan-interfaces. */
export interface WanInterfaceState {
    name?: string;
    ip?: string;
    mask?: string | number;
    link?: boolean;
    is_public_ip?: boolean;
}

/** Interface state keyed by FortiGate hostname, then by interface name. */
export interface WanInterfacesByHostname {
    [hostname: string]: { [interfaceName: string]: WanInterfaceState };
}

export interface NetworkStateInput {
    fortigates: { conn_status: number; vpn_status?: string; hostname?: string }[];
    cradlepoints: { name: string; state: string; ipv4_address: string }[];
    merakiSwitchStatuses: { status: string; publicIp: string }[];
    /** Live wan1/wan2 state. Omit it when the view never asks for it. */
    wanInterfacesByHostname?: WanInterfacesByHostname;
    /** False when the VPN lookup was attempted and failed. */
    vpnDataAvailable?: boolean;
    /** False when the interface lookup was attempted and failed. */
    wanInterfaceDataAvailable?: boolean;
}

export interface NetworkStateEvaluation {
    state: NetworkState;
    /** Human readable state name, e.g. "EM Failover". */
    label: string;
    /** One sentence saying what the state means for the store. */
    summary: string;
    /** The specific observations that produced the state. */
    evidence: string[];
    /** Newline joined label, summary and evidence, for use as a tooltip. */
    tooltip: string;
}

interface LteEgressMatch {
    cradlepointName: string;
    ip: string;
}

interface WanInterfaceVerdict {
    state: NetworkState;
    summary: string;
    evidence: string[];
}

/**
 * Packages a state, a one line explanation of what it means, and the observations
 * behind it into the shape the network state badges render as a tooltip. Exported so
 * views that reach a state by their own route can still explain themselves.
 */
export function buildNetworkStateEvaluation(
    state: NetworkState,
    summary: string,
    evidence: string[]
): NetworkStateEvaluation {
    const label = NETWORK_STATE_LABELS[state];
    const lines = [`${label} — ${summary}`, ...evidence.map(line => `• ${line}`)];
    return { state, label, summary, evidence, tooltip: lines.join('\n') };
}
function describeFortigates(fortigates: { hostname?: string }[]): string {
    const named = fortigates.map(fg => fg.hostname).filter(Boolean);
    if (named.length === 0) {
        return fortigates.length === 1 ? 'The FortiGate' : `All ${fortigates.length} FortiGates`;
    }
    if (named.length === 1) {
        return `FortiGate ${named[0]}`;
    }
    return `FortiGates ${named.join(', ')}`;
}

/**
 * Finds a WAN2 CradlePoint whose own address is what the store's switches report as
 * their public IP. That equality is a direct measurement that traffic is leaving
 * through the LTE modem rather than the primary circuit.
 */
function findLteEgressMatch(
    cradlepoints: { name: string; state: string; ipv4_address: string }[],
    merakiSwitchStatuses: { status: string; publicIp: string }[]
): LteEgressMatch | null {
    const onlineSwitchPublicIps = new Set(
        merakiSwitchStatuses
            .filter(m => m.status === DEVICE_STATUS.ONLINE && m.publicIp)
            .map(m => m.publicIp)
    );
    if (onlineSwitchPublicIps.size === 0) {
        return null;
    }

    const match = cradlepoints.find(cp =>
        cp.name?.includes('WAN2')
        && cp.state === DEVICE_STATUS.ONLINE
        && cp.ipv4_address
        && onlineSwitchPublicIps.has(cp.ipv4_address)
    );

    return match ? { cradlepointName: match.name, ip: match.ipv4_address } : null;
}

function isInterfaceUp(wanInterface?: WanInterfaceState): boolean {
    if (!wanInterface) {
        return false;
    }
    if (wanInterface.link === false) {
        return false;
    }
    return !!wanInterface.ip && wanInterface.ip !== '0.0.0.0';
}

function describeDownInterface(name: string, wanInterface?: WanInterfaceState): string {
    if (!wanInterface) {
        return `Its ${name} interface was not reported.`;
    }
    if (wanInterface.link === false) {
        return `Its ${name} interface link is down.`;
    }
    if (!wanInterface.ip || wanInterface.ip === '0.0.0.0') {
        return `Its ${name} interface has no IP address assigned.`;
    }
    return `Its ${name} interface is not carrying traffic.`;
}

function evaluateWanInterfaces(
    onlineFortigates: { hostname?: string }[],
    wanInterfacesByHostname?: WanInterfacesByHostname
): WanInterfaceVerdict | null {
    if (!wanInterfacesByHostname) {
        return null;
    }

    for (const fortigate of onlineFortigates) {
        const interfaces = fortigate.hostname ? wanInterfacesByHostname[fortigate.hostname] : undefined;
        if (!interfaces) {
            continue;
        }

        const wan1 = interfaces['wan1'];
        const wan2 = interfaces['wan2'];

        if (isInterfaceUp(wan1)) {
            return {
                state: NETWORK_STATE.ON_WAN1,
                summary: 'the store is using its primary circuit.',
                evidence: [`Its wan1 interface is up with IP ${wan1.ip}, which is the address the store uses to reach the internet.`],
            };
        }

        if (isInterfaceUp(wan2)) {
            return {
                state: NETWORK_STATE.ON_WAN2,
                summary: 'the store has failed over to the WAN2 backup circuit.',
                evidence: [
                    describeDownInterface('wan1', wan1),
                    `Its wan2 interface is up with IP ${wan2.ip}, so traffic is leaving over the backup circuit.`,
                ],
            };
        }
    }

    return null;
}

function appendVpnCorroboration(
    evidence: string[],
    onlineFortigates: { vpn_status?: string }[],
    vpnDataAvailable?: boolean
): void {
    const tunnelName = onlineFortigates.map(fg => fg.vpn_status).find(hasResolvedVpnName);
    if (tunnelName) {
        evidence.push(`Its active IPsec tunnel is ${tunnelName}.`);
        return;
    }
    if (vpnDataAvailable === false) {
        evidence.push('The active VPN tunnel could not be read, so the interface state above was used instead.');
    }
}

function evaluateWithoutOnlineFortigate(
    fortigates: { hostname?: string }[],
    cradlepoints: { name: string; state: string; ipv4_address: string }[],
    lteEgress: LteEgressMatch | null
): NetworkStateEvaluation {
    const evidence: string[] = [];

    if (fortigates.length === 0) {
        evidence.push('No FortiGate is reporting to FortiManager for this store.');
    } else {
        const single = fortigates.length === 1;
        const clause = single ? 'is offline in FortiManager, so it is' : 'are offline in FortiManager, so they are';
        evidence.push(`${describeFortigates(fortigates)} ${clause} not routing traffic.`);
    }

    if (lteEgress) {
        evidence.push(`WAN2 CradlePoint ${lteEgress.cradlepointName} is online with IP ${lteEgress.ip}.`);
        evidence.push(`The Meraki switches report public IP ${lteEgress.ip}, which matches that CradlePoint, so the store is reaching the internet through the CradlePoint LTE modem instead of the FortiGate.`);
        return buildNetworkStateEvaluation(
            NETWORK_STATE.EM_FAILOVER,
            'the store is online through the CradlePoint LTE modem, bypassing the FortiGate entirely.',
            evidence
        );
    }

    // Neither half of the EM Failover check held, so say which one.
    const onlineWan2Cradlepoint = cradlepoints.find(
        cp => cp.name?.includes('WAN2') && cp.state === DEVICE_STATUS.ONLINE
    );
    if (!onlineWan2Cradlepoint) {
        evidence.push('No WAN2 CradlePoint is online either, so there is no LTE path out of the store.');
    } else {
        evidence.push(`WAN2 CradlePoint ${onlineWan2Cradlepoint.name} is online, but no online Meraki switch reports its address (${onlineWan2Cradlepoint.ipv4_address}) as their public IP, so the switches are not egressing through it.`);
    }

    return buildNetworkStateEvaluation(NETWORK_STATE.OFFLINE, 'nothing at the store is reachable.', evidence);
}

/**
 * Works out which circuit a store is using, and records why.
 *
 * Signals are weighed strongest first:
 *   1. Switch public IP matching the WAN2 CradlePoint -- a direct measurement of
 *      where traffic actually leaves the store.
 *   2. Live wan1/wan2 interface state read off the FortiGate.
 *   3. The active IPsec tunnel name, which only reflects the circuit indirectly and
 *      varies by naming convention.
 *
 * When an online FortiGate has none of those, the result is UNKNOWN rather than
 * ON_WAN1, but only if the caller actually asked for telemetry and it came back
 * empty. Views that never request it (the chain-wide table reads a cache with no
 * tunnel or interface data) keep assuming the primary circuit.
 */
export function evaluateNetworkState(input: NetworkStateInput): NetworkStateEvaluation {
    const fortigates = input.fortigates || [];
    const cradlepoints = input.cradlepoints || [];
    const merakiSwitchStatuses = input.merakiSwitchStatuses || [];

    const onlineFortigates = fortigates.filter(fg => fg.conn_status === 1);
    const lteEgress = findLteEgressMatch(cradlepoints, merakiSwitchStatuses);

    if (onlineFortigates.length === 0) {
        return evaluateWithoutOnlineFortigate(fortigates, cradlepoints, lteEgress);
    }

    const verb = onlineFortigates.length === 1 ? 'is' : 'are';
    const evidence = [`${describeFortigates(onlineFortigates)} ${verb} online in FortiManager.`];

    if (lteEgress) {
        evidence.push(`The Meraki switches report public IP ${lteEgress.ip}, which matches WAN2 CradlePoint ${lteEgress.cradlepointName}, so traffic is leaving through the LTE modem rather than the primary circuit.`);
        return buildNetworkStateEvaluation(NETWORK_STATE.ON_WAN2, 'the store has failed over to the WAN2 backup circuit.', evidence);
    }

    const interfaceVerdict = evaluateWanInterfaces(onlineFortigates, input.wanInterfacesByHostname);
    if (interfaceVerdict) {
        evidence.push(...interfaceVerdict.evidence);
        appendVpnCorroboration(evidence, onlineFortigates, input.vpnDataAvailable);
        return buildNetworkStateEvaluation(interfaceVerdict.state, interfaceVerdict.summary, evidence);
    }

    if (input.wanInterfaceDataAvailable === false) {
        evidence.push('The live wan1/wan2 interface state could not be read from the FortiGate.');
    }

    const backupTunnel = onlineFortigates.find(fg => isBackupVpnName(fg.vpn_status));
    if (backupTunnel) {
        evidence.push(`Its active IPsec tunnel is ${backupTunnel.vpn_status}, which is a WAN2 backup tunnel.`);
        return buildNetworkStateEvaluation(NETWORK_STATE.ON_WAN2, 'the store has failed over to the WAN2 backup circuit.', evidence);
    }

    const primaryTunnel = onlineFortigates.find(fg => hasResolvedVpnName(fg.vpn_status));
    if (primaryTunnel) {
        evidence.push(`Its active IPsec tunnel is ${primaryTunnel.vpn_status}, which runs over the primary circuit.`);
        return buildNetworkStateEvaluation(NETWORK_STATE.ON_WAN1, 'the store is using its primary circuit.', evidence);
    }

    if (input.vpnDataAvailable === false || input.wanInterfaceDataAvailable === false) {
        evidence.push('No active VPN tunnel was reported for it either.');
        evidence.push('Without interface or tunnel data there is no way to tell which circuit is carrying traffic, so this is deliberately not reported as healthy.');
        return buildNetworkStateEvaluation(
            NETWORK_STATE.UNKNOWN,
            'the FortiGate is reachable, but which circuit it is using could not be determined.',
            evidence
        );
    }

    evidence.push('This view does not collect tunnel or interface data, so the primary circuit is assumed.');
    return buildNetworkStateEvaluation(NETWORK_STATE.ON_WAN1, 'the store is using its primary circuit.', evidence);
}

/**
 * Determines the overall network state for a store based on FortiGate VPN status,
 * CradlePoint failover, and Meraki switch connectivity.
 */
export function determineNetworkState(input: NetworkStateInput): NetworkState {
    return evaluateNetworkState(input).state;
}
